import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  DocumentReference
} from "firebase/firestore";
import { firestore as db } from "@/utils/firebase";
import { User } from "./types";

// Get typed document reference
const getUserRef = (userId: string): DocumentReference<User> =>
  doc(db, "users", userId) as DocumentReference<User>;

/**
 * Create the user document
 */
export const createUser = async (
  userId: string,
  data: Omit<User, "createdAt" | "updatedAt">
): Promise<void> => {
  await setDoc(getUserRef(userId), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

/**
 * Get a single user document by ID
 */
export const getUser = async (userId: string): Promise<User | null> => {
  const docSnap = await getDoc(getUserRef(userId));
  return docSnap.exists() ? docSnap.data() : null;
};

/**
 * Update existing user fields (fails if user doesn't exist)
 */
export const updateUser = async (
  userId: string,
  updates: Partial<Omit<User, "createdAt" | "updatedAt">>
): Promise<void> => {
  await updateDoc(getUserRef(userId), {
    ...updates,
    updatedAt: serverTimestamp()
  });
};

/**
 * Update user consent with automatic consentAt timestamp
 */
export const updateUserConsent = async (
  userId: string,
  consent: {
    hasConsented: boolean;
    consentVersion?: string;
  }
): Promise<void> => {
  await updateDoc(getUserRef(userId), {
    consent: {
      ...consent,
      consentedAt: serverTimestamp()
    },
    updatedAt: serverTimestamp()
  });
};

/**
 * Update user settings (voice, language, etc.)
 */
export const updateUserSettings = async (
  userId: string,
  settings: NonNullable<User["settings"]>
): Promise<void> => {
  // Flatten settings for update to avoid overwriting other nested fields if they exist
  const updates: Record<string, any> = {
    updatedAt: serverTimestamp()
  };

  if (settings.voice) {
    updates["settings.voice"] = settings.voice;
  }
  if (settings.language) {
    updates["settings.language"] = settings.language;
  }

  await updateDoc(getUserRef(userId), updates);
};

/**
 * Get user settings
 */
export const getUserSettings = async (
  userId: string
): Promise<User["settings"] | undefined> => {
  const user = await getUser(userId);
  return user?.settings;
};
